import { formatDistanceToNow, format, parseISO, isAfter, isBefore, subMinutes } from 'date-fns';

/**
 * Safely formats a date for display, preventing future dates from showing as "tomorrow" or "in X hours"
 * This handles timezone differences and clock synchronization issues between client and server
 */
export function formatSafeRelativeTime(dateInput: string | Date, options?: { addSuffix?: boolean }): string {
  try {
    // Handle null, undefined, or empty strings
    if (!dateInput || dateInput === '') {
      return 'Unknown date';
    }
    
    // Parse the date if it's a string
    const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
    const now = new Date();
    
    // Add a reasonable buffer (30 seconds) to account for minor clock differences
    // This prevents console spam from millisecond-level differences
    const bufferMs = 30 * 1000; // 30 seconds
    const timeDiff = date.getTime() - now.getTime();
    
    // If the date is significantly in the future (more than 30 seconds), treat it as "just now"
    // Only log if it's more than 1 minute in the future to avoid spam
    if (timeDiff > bufferMs) {
      if (timeDiff > 60 * 1000) { // Only log if more than 1 minute in future
        console.warn(`[DateFormatter] Significant future date detected: ${date.toISOString()}, current time: ${now.toISOString()}, diff: ${Math.round(timeDiff/1000)}s`);
      }
      return options?.addSuffix ? 'just now' : 'now';
    }
    
    // If the date is more than 1 year old, show absolute date
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(now.getFullYear() - 1);
    if (isBefore(date, oneYearAgo)) {
      return format(date, 'MMM d, yyyy');
    }
    
    // Otherwise, show relative time
    return formatDistanceToNow(date, options);
  } catch (error) {
    console.error('[DateFormatter] Error formatting date:', error, 'Input:', dateInput);
    // Fallback to showing the raw date string or a safe default
    if (typeof dateInput === 'string') {
      try {
        return format(parseISO(dateInput), 'MMM d, yyyy HH:mm');
      } catch {
        return 'Unknown date';
      }
    }
    return 'Unknown date';
  }
}

/**
 * Formats a date as an absolute timestamp
 * Useful for tooltips or when exact time is needed
 */
export function formatAbsoluteTime(dateInput: string | Date): string {
  try {
    // Handle null, undefined, or empty strings
    if (!dateInput || dateInput === '') {
      return 'Unknown date';
    }
    
    const inputDate = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
    
    // Check if the parsed date is valid
    if (isNaN(inputDate.getTime())) {
      return 'Unknown date';
    }
    
    const safeDate = getSafeDisplayDate(inputDate);
    return format(safeDate, 'MMM d, yyyy \'at\' h:mm a');
  } catch (error) {
    console.error('[DateFormatter] Error formatting absolute date:', error, 'Input:', dateInput);
    return 'Unknown date';
  }
}

/**
 * Checks if a date is in the future (with a small buffer for clock differences)
 */
export function isDateInFuture(dateInput: string | Date, bufferSeconds: number = 30): boolean {
  try {
    const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
    const now = new Date();
    const timeDiff = date.getTime() - now.getTime();
    // Date is in future if it's more than bufferSeconds ahead
    return timeDiff > (bufferSeconds * 1000);
  } catch (error) {
    console.error('[DateFormatter] Error checking future date:', error, 'Input:', dateInput);
    return false;
  }
}

/**
 * Gets a display-safe date that ensures no future dates are shown
 * Returns the minimum of the input date and current time
 */
export function getSafeDisplayDate(dateInput: string | Date): Date {
  try {
    const date = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
    // Check if the parsed date is valid
    if (isNaN(date.getTime())) {
      console.warn('[DateFormatter] Invalid date detected, returning current time');
      return new Date();
    }
    const now = new Date();
    // Use a 30-second buffer for consistency with formatSafeRelativeTime
    const timeDiff = date.getTime() - now.getTime();
    const bufferMs = 30 * 1000; // 30 seconds
    return timeDiff > bufferMs ? now : date;
  } catch (error) {
    console.error('[DateFormatter] Error getting safe date:', error, 'Input:', dateInput);
    return new Date();
  }
}

/**
 * Formats a date with both relative and absolute time
 * Useful for showing "2 hours ago (Jan 23, 2025 at 3:45 PM)"
 */
export function formatDetailedTime(dateInput: string | Date): string {
  const relative = formatSafeRelativeTime(dateInput, { addSuffix: true });
  const absolute = formatAbsoluteTime(dateInput);
  return `${relative} (${absolute})`;
}